import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 30 * 60 * 1000; // auto sign-out after 30 minutes of inactivity

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const idleRef = useRef(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const logout = useCallback(async (reason) => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    if (reason === "idle") {
      try { sessionStorage.setItem("logout_reason", "idle"); } catch {}
    }
  }, []);

  // Idle session timeout: reset a timer on activity while signed in.
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      clearTimeout(idleRef.current);
      idleRef.current = setTimeout(() => logout("idle"), IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(idleRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) return { ok: true, mfa: true, challengeId: data.challenge_id, channel: data.channel, dest: data.dest };
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyOtp = async (challengeId, code) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { challenge_id: challengeId, code });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resendOtp = async (challengeId) => {
    try {
      const { data } = await api.post("/auth/resend-otp", { challenge_id: challengeId });
      return { ok: true, message: data.message, channel: data.channel, dest: data.dest };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, phone });
      if (data.access_token) {
        setUser(data.user);
        return { ok: true, immediate: true };
      }
      return { ok: true, immediate: false, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const activate = async (token) => {
    try {
      const { data } = await api.post("/auth/activate", { token });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resendActivation = async (email) => {
    try {
      const { data } = await api.post("/auth/resend-activation", { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const isRole = (...roles) => user && roles.includes(user.role);

  const setMyPhone = async (phone) => {
    try {
      const { data } = await api.post("/auth/me/phone", { phone });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyOtp, resendOtp, register, logout, activate, resendActivation, isRole, setMyPhone, reload: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
